import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import {
  FileText, Spinner, DownloadSimple, Receipt, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import Pagination from "../components/Pagination";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const InvoicesPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const fetchInvoices = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customer/invoices`);
      setInvoices(res.data);
    } catch { /* no invoices yet */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchInvoices(); }, [fetchInvoices]);

  const handleDownload = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/api/invoices/${invoiceId}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `fatura-${invoiceId.slice(0, 8)}.pdf`;
      link.click();
    } catch {
      toast.error("PDF indirme basarisiz");
    }
  };

  const handleDeleteInvoice = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${API_URL}/api/admin/invoices/${deleteTarget.id}`);
      toast.success(t("invoices.deleted_success"));
      setInvoices(prev => prev.filter(inv => inv.id !== deleteTarget.id));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally {
      setDeleteDialog(false);
      setDeleteTarget(null);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner size={32} className="animate-spin text-indigo-600" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="invoices-page">
      <div>
        <h1 className="text-2xl font-heading font-bold text-slate-900">{t("fixed_expenses.invoices_tab")}</h1>
        <p className="text-slate-500 mt-1">Gecmis fatura ve odeme kayitlariniz</p>
      </div>

      {invoices.length === 0 ? (
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Receipt size={56} className="text-slate-300 mb-4" />
            <p className="text-lg font-medium text-slate-600">Henuz fatura yok</p>
            <p className="text-sm text-slate-400 mt-1">Odeme islemleri basladiktan sonra faturalariniz burada gorunecektir</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-heading flex items-center gap-2">
              <FileText size={20} className="text-indigo-600" /> Fatura Gecmisi
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>{t("common.date")}</TableHead>
                    <TableHead>{t("invoices.invoice_no")}</TableHead>
                    <TableHead>{t("common.tenant")}</TableHead>
                    <TableHead>{t("common.amount")}</TableHead>
                    <TableHead>{t("common.status")}</TableHead>
                    <TableHead className="w-20">{t("common.actions")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.slice((page - 1) * pageSize, page * pageSize).map((inv) => (
                    <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                      <TableCell>{new Date(inv.created_at).toLocaleDateString("tr-TR")}</TableCell>
                      <TableCell className="font-mono text-sm">{inv.invoice_number || inv.id.slice(0, 8)}</TableCell>
                      <TableCell>{inv.tenant_name || "-"}</TableCell>
                      <TableCell className="font-medium">₺{(inv.amount || 0).toFixed(2)}</TableCell>
                      <TableCell>
                        <Badge className={inv.status === "paid" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}>
                          {inv.status === "paid" ? t("fixed_expenses.paid") : "Beklemede"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button size="sm" variant="ghost" className="text-indigo-600"
                            onClick={() => handleDownload(inv.id)} data-testid={`download-invoice-${inv.id}`}>
                            <DownloadSimple size={16} />
                          </Button>
                          {user?.role === "admin" && (
                            <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700 h-7 w-7 p-0"
                              onClick={() => { setDeleteTarget(inv); setDeleteDialog(true); }}
                              data-testid={`delete-invoice-${inv.id}`}
                              title={t("invoices.delete")}>
                              <Trash size={14} />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            {invoices.length > pageSize && (
              <div className="pt-4">
                <Pagination total={invoices.length} page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Invoice Confirmation */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("invoices.delete_confirm_title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteTarget && (
                <span>{deleteTarget.tenant_name || deleteTarget.invoice_number} - ₺{(deleteTarget.amount || 0).toFixed(2)}<br/></span>
              )}
              {t("invoices.delete_confirm_desc")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteInvoice} className="bg-red-600 hover:bg-red-700" data-testid="confirm-delete-invoice">
              {t("common.delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default InvoicesPage;
